import { useCallback, useMemo, useState } from 'react';

import { getFramePrice } from '../../../components/Checkout/Framing';
import { FramingSelections } from '../../../components/Checkout/Framing/types';
import {
  OrderReviewItem,
  OrderReviewSummaryProps,
} from '../../../components/Checkout/OrderReview';
import { CurrencyCode } from '../../../types/Locations';
import { computeOrderTotals } from '../../../utils/checkout/utils';
import { CheckoutStep } from '../types';
import { CheckoutOrderTotals } from '../utils/orderTotals';
import { CheckoutWork } from '../utils/workView';

export type CheckoutSummaryProps = Omit<
  OrderReviewSummaryProps,
  'showPreview' | 'maxQuantityNote' | 'cardHold'
>;

export type UseCheckoutOrderValuesArgs = {
  allowMultipleSelections: boolean;
  bid?: string;
  currencyCode: CurrencyCode;
  framableWorkIds: ReadonlySet<string>;
  framingSelections: FramingSelections;
  maxQuantity: number;
  quote: CheckoutOrderTotals | null;
  selectedWorks: CheckoutWork[];
  showPreference: boolean;
  step: CheckoutStep;
};

export type CheckoutOrderValues = {
  orderedItems: OrderReviewItem[];
  orderTotals: CheckoutOrderTotals;
  summaryProps: CheckoutSummaryProps;
};

export const useCheckoutOrderValues = ({
  allowMultipleSelections,
  bid,
  currencyCode,
  framableWorkIds,
  framingSelections,
  maxQuantity,
  quote,
  selectedWorks,
  showPreference,
  step,
}: UseCheckoutOrderValuesArgs): CheckoutOrderValues => {
  const [openSummaryIds, setOpenSummaryIds] = useState<Record<string, boolean>>(
    {},
  );
  const [openSummaryStep, setOpenSummaryStep] = useState<CheckoutStep>(step);

  if (openSummaryStep !== step) {
    setOpenSummaryStep(step);
    setOpenSummaryIds({});
  }

  const quantity = allowMultipleSelections ? selectedWorks.length : maxQuantity;

  const orderedItems = useMemo<OrderReviewItem[]>(
    () =>
      selectedWorks.map((work) => ({
        hasFrameOptions: framableWorkIds.has(work.id),
        id: work.id,
        image: work.imageSrc,
        price: work.price * (work.quantity ?? 1),
        quantity: work.quantity,
        title: work.title,
      })),
    [framableWorkIds, selectedWorks],
  );

  const orderTotals = useMemo<CheckoutOrderTotals>(
    () =>
      quote ?? {
        ...computeOrderTotals({
          currencyCode,
          maxQuantity: quantity,
          prices: selectedWorks.map(
            (work) =>
              work.price * (work.quantity ?? 1) +
              getFramePrice(framingSelections[work.id] ?? null),
          ),
        }),
        freeShipping:
          selectedWorks.length > 0 &&
          selectedWorks.every((work) => work.freeShipping),
      },
    [currencyCode, framingSelections, quantity, quote, selectedWorks],
  );

  const onToggleSummary = useCallback((itemId: string) => {
    setOpenSummaryIds((previous) => ({
      ...previous,
      [itemId]: !previous[itemId],
    }));
  }, []);

  const summaryProps = useMemo<CheckoutSummaryProps>(
    () => ({
      allowMultipleSelections,
      bid,
      currencyCode,
      framingSelections,
      freeShipping: orderTotals.freeShipping,
      isEstimate: orderTotals.isEstimate,
      maxOrderValue: orderTotals.maxOrderValue,
      maxQuantity: quantity,
      onToggleSummary,
      openSummaryIds,
      orderValue: orderTotals.orderValue,
      orderedItems,
      shippingCost: orderTotals.shippingCost,
      showPreference,
      taxRate: orderTotals.taxRate,
      taxValue: orderTotals.taxValue,
    }),
    [
      allowMultipleSelections,
      bid,
      currencyCode,
      framingSelections,
      onToggleSummary,
      openSummaryIds,
      orderTotals,
      orderedItems,
      quantity,
      showPreference,
    ],
  );

  return useMemo(
    () => ({ orderedItems, orderTotals, summaryProps }),
    [orderedItems, orderTotals, summaryProps],
  );
};
